import { useEffect } from 'react';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
// Redux
import { useDispatch, useSelector } from 'react-redux';
// Pages
import appPages from './pages';
// Utils
import { fetchWithoutToken } from '../../utils/fetch';
// Slice
import { setActiveLoginModal } from '../../app/features/userSlice';
import { setInterestingLinks } from '../../app/features/interestingLinksSlice';
import { setAllArtSources } from '../../app/features/artSlice';
import { setAllBooksSources } from '../../app/features/booksSlice';
import { setAllPatentsSources } from '../../app/features/patentsSlice';
import { setAllLibrarySources } from '../../app/features/librarySlice';
import {
	setBaseNews,
	setResetBaseNews,
	setIsNewsLoading,
} from '../../app/features/newsSlice';
import {
	setPublicToken,
	setPublicTokenExpires,
} from '../../app/features/authSlice';
import {
	setDatabases,
	setInstitutionCode,
	setInstitutionLogo,
	setTopics,
	setRequestLogin,
	setUniqueLogin,
	setAssignedLogin,
	setEbooksAccess,
	setIsMedicalInstitution,
	setLanguageInstitution,
	setIsActive,
	setInstitutionState,
} from '../../app/features/institutionSlice';
// Services
import { getNews } from '../../services/getNews';
import { getInterestingLinks } from '../../services/getInterestingLinks';
import { getInstitutionTopicsAndDatabases } from '../../services/getInstitutionTopicsAndDatabases';
import { isCosmetology } from '../../utils/isCosmetology';
import { sectionsToRemoveInCosmetology } from '../../services/getSectionsInstitutions';
import { isTokenExpired } from '../../utils/isTokenExpired';
// Translate
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import { libraryStates } from '../../utils/libraryStates';
import { filterBlockedNewsItems } from '../../utils/newsFilters';

const Router = () => {
	const { i18n } = useTranslation();
	const { languageInstitution, isActive, institutionState } = useSelector(
		(state) => state.institution,
	);
	const publicPages = [
		'',
		'help',
		'login',
		'recover',
		'signup',
		'gale',
		'saml-callback',
		'd2l-callback',
		'login-with-wordpress',
	];
	const hasPriorityInstitutionState = [
		libraryStates.CANCELLED,
		libraryStates.DEMO_CANCELLED,
		libraryStates.DUPLICATE,
	].includes(institutionState);

	useEffect(() => {
		i18n.changeLanguage(languageInstitution);
		dispatch(setLanguageInstitution(languageInstitution));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [languageInstitution]);

	const navigate = useNavigate();
	const dispatch = useDispatch();
	const { pathname } = useLocation();
	const currentlyRoute = pathname.replace(/^\/+|\/+$/g, '');
	const isGaleRoute = /(^|\/)gale(\/|$)/i.test(pathname);

	// Datos de autenticación
	const USERNAME = process.env.REACT_APP_AUTH_USERNAME;
	const PASSWORD = process.env.REACT_APP_PASSWORD;
	const ENVIRONMENT = process.env.REACT_APP_ENVIRONMENT;
	const INSTITUTION_CODE = process.env.REACT_APP_INSTITUTION_CODE;
	const isCosmetologyInstitution = isCosmetology(INSTITUTION_CODE);
	const isTodayOrPreviousDay = (rawDate) => {
		if (!rawDate) return false;

		let targetDate = null;
		if (typeof rawDate === 'string' && /^\d{4}-\d{2}-\d{2}/.test(rawDate)) {
			const [year, month, day] = rawDate.slice(0, 10).split('-').map(Number);
			targetDate = new Date(year, month - 1, day);
		} else {
			const parsed = new Date(rawDate);
			if (!Number.isNaN(parsed.getTime())) {
				targetDate = new Date(
					parsed.getFullYear(),
					parsed.getMonth(),
					parsed.getDate(),
				);
			}
		}

		if (!targetDate) return false;

		const today = new Date();
		const todayAtMidnight = new Date(
			today.getFullYear(),
			today.getMonth(),
			today.getDate(),
		);

		const diffInDays = Math.round(
			(todayAtMidnight - targetDate) / (1000 * 60 * 60 * 24),
		);

		// 0 => today, >0 => any previous day, <0 => future
		return diffInDays >= 0;
	};

	const loadInstitutionData = async (code) => {
		dispatch(setIsNewsLoading(true));
		try {
			const newsResponse = await getNews(code);
			dispatch(setResetBaseNews());
			const newsFiltered = newsResponse.data.filter(
				(item) => item.title !== '[Removed]',
			);
			dispatch(setBaseNews(filterBlockedNewsItems(newsFiltered)));
		} catch (error) {
			dispatch(setBaseNews([]));
		} finally {
			dispatch(setIsNewsLoading(false));
		}

		try {
			const resourcesResponse = await getInterestingLinks(code);
			dispatch(setInterestingLinks(resourcesResponse.data.data));
		} catch (error) {
			dispatch(setInterestingLinks([]));
		}

		try {
			const topicsResponse = await getInstitutionTopicsAndDatabases(code);
			dispatch(setTopics(topicsResponse.data.categories));
			dispatch(setDatabases(topicsResponse.data.sources));
		} catch (error) {
			dispatch(setTopics([]));
			dispatch(setDatabases([]));
		}
	};

	const authenticateUser = async () => {
		try {
			const response = await fetchWithoutToken.post('/auth', {
				username: USERNAME,
				password: PASSWORD,
				code: ENVIRONMENT === 'PRODUCTION' ? null : INSTITUTION_CODE,
			});

			const {
				access_token,
				expires_in,
				code,
				logo,
				requestLoginInstitution,
				uniqueLoginInstitution,
				// all sources
				academic_sources,
				books_sources,
				gallery_sources,
				patents_sources,
				assignedLogin,
				ebooks,
				is_medical,
				language,
				is_active,
				olib_state_id,
				date_demo,
			} = response.data;

			const institutionStateToSet = isTodayOrPreviousDay(date_demo)
				? libraryStates.DEMO_CANCELLED
				: olib_state_id;

			const uniqueBooksSources = [...new Set(books_sources)];

			localStorage.setItem('token', access_token);
			localStorage.setItem('expires_in', expires_in);
			dispatch(setInstitutionCode(code));
			dispatch(setAssignedLogin(assignedLogin));
			dispatch(setInstitutionLogo(logo));
			dispatch(setIsMedicalInstitution(is_medical));
			dispatch(setPublicToken(access_token));
			dispatch(setPublicTokenExpires(expires_in));
			dispatch(setRequestLogin(requestLoginInstitution));
			dispatch(setUniqueLogin(uniqueLoginInstitution));
			dispatch(setAllLibrarySources(academic_sources));
			dispatch(setAllBooksSources(uniqueBooksSources));
			dispatch(setAllArtSources(gallery_sources));
			dispatch(setAllPatentsSources(patents_sources));
			dispatch(setEbooksAccess(ebooks));
			dispatch(setIsActive(is_active));
			dispatch(setInstitutionState(institutionStateToSet));
			languageInstitution === null &&
				dispatch(setLanguageInstitution(language));
			//  Solicitando datos de la institucion
			await loadInstitutionData(code);
		} catch (error) {
			console.error('Error during authentication', error);
		}
	};

	const { isUserLoggedInAndHasLoggingSettingsActive } = useAuth();

	useEffect(() => {
		if (isGaleRoute) {
			if (currentlyRoute === '' || isTokenExpired(localStorage.expires_in)) {
				authenticateUser();
			}
			return;
		}

		if (!isActive && currentlyRoute !== '' && !isGaleRoute) {
			dispatch(setActiveLoginModal(true));
			navigate('/');
		}
		if (hasPriorityInstitutionState) {
			dispatch(setActiveLoginModal(true));
			navigate('/');
		}

		if (currentlyRoute === '' || isTokenExpired(localStorage.expires_in)) {
			authenticateUser();
		}

		if (isCosmetologyInstitution) {
			if ([...sectionsToRemoveInCosmetology].includes(currentlyRoute)) {
				navigate('/media');
				return;
			}

			(currentlyRoute === 'media' || currentlyRoute === '') &&
				authenticateUser();
			return;
		}

		if (!isUserLoggedInAndHasLoggingSettingsActive()) {
			if (publicPages.includes(currentlyRoute) || isGaleRoute) {
				return;
			}

			dispatch(setActiveLoginModal(true));
			navigate('/');
			return;
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		currentlyRoute,
		isCosmetologyInstitution,
		institutionState,
		hasPriorityInstitutionState,
	]);

	const pageRoutes = appPages.map(({ path, title, element }) => {
		return <Route key={title} path={`/${path}`} element={element} />;
	});

	return <Routes>{pageRoutes}</Routes>;
};

export default Router;
